/**
 * SessionKeepAlive - Background component for Wialon session management
 * 
 * This component runs in the background and:
 * - Performs keep-alive pings every 45 seconds
 * - Reads session data directly from cookies (like old frontend)
 * - Redirects to /sorry page on session error
 */

import { useEffect, useRef } from 'react';
import Cookies from 'universal-cookie';
import WialonService from '../Network/Wialon/WialonService';

const cookies = new Cookies();
const KEEP_ALIVE_INTERVAL = 45000; // 45 seconds

/**
 * SessionKeepAlive Component
 * Renders nothing but manages session lifecycle in background
 */
function SessionKeepAlive() {
    const intervalRef = useRef(null);

    useEffect(() => {
        console.log('[SessionKeepAlive] Component mounted, starting interval...');

        // Perform keep-alive ping
        const performKeepAlive = async () => {
            const baseUrl = cookies.get('baseUrl');
            const sid = cookies.get('sid');

            console.log('[SessionKeepAlive] Checking session - baseUrl:', baseUrl, 'sid:', sid);

            // Don't ping if no credentials or if sid is null/string "null"
            if (!baseUrl || !sid || sid === 'null') {
                console.log('[SessionKeepAlive] No valid session credentials');
                return;
            }

            try {
                console.log('[SessionKeepAlive] Pinging Wialon at', new Date().toLocaleTimeString());
                const result = await WialonService.keepAlive(baseUrl, sid);

                // Check if response has tm (timestamp) - indicates success
                if (result?.tm !== undefined) {
                    cookies.set('active_session', 'yes', { path: '/' });
                    console.log('[SessionKeepAlive] Keep-alive successful');
                }
            } catch (error) {
                console.warn('[SessionKeepAlive] Keep-alive failed:', error.message);

                // Check if it's an authentication error (invalid session)
                if (error.isAuthError || error.code === 1) {
                    console.error('[SessionKeepAlive] Authentication error detected, redirecting to /sorry');
                    cookies.set('active_session', 'no', { path: '/' });
                    cookies.set('sid', null, { path: '/' });
                    // Use window.location for immediate redirect
                    window.location.href = '/sorry';
                    return;
                }
            }
        };

        // Initial keep-alive after 2 seconds
        const initialTimeout = setTimeout(() => {
            performKeepAlive();
        }, 2000);

        // Setup interval
        intervalRef.current = setInterval(performKeepAlive, KEEP_ALIVE_INTERVAL);
        console.log('[SessionKeepAlive] Interval started (every', KEEP_ALIVE_INTERVAL / 1000, 'seconds)');

        return () => {
            console.log('[SessionKeepAlive] Component unmounting, clearing interval');
            clearTimeout(initialTimeout);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, []);

    // This component renders nothing - it's just for side effects
    return null;
}

export default SessionKeepAlive;

